// ADR-198: Reports → Scheduled Jobs — one row per radar-scheduler job, grouped by category.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Tooltip,
} from '@mui/material';
import { getChipSx } from '../../../constants/chipPalette';
import {
  JOB_STATE_LABELS, JOB_STATE_COLORS, JOB_OUTCOME_LABELS, JOB_OUTCOME_COLORS, RUN_HISTORY_REPORTS,
} from '../../../constants/scheduledJobs';
import { SYNC_TRIGGER_COLORS } from '../../../constants/ingestionSyncRuns';
import { describeTrigger, formatDuration } from '../../../utils/ingestionSyncRuns';
import { describeSchedule, formatRelative, groupByCategory } from '../../../utils/scheduledJobs';
import { TableWrapper, TwoLine, TwoLinePrimary, TwoLineSecondary } from '../DirectManagerDiscrepancies/styles';
import { LinkButton } from '../IngestionSyncRuns/styles';
import { FEATURE_FLAGS } from '../../../constants/featureFlags';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

// The subtitle is the job's own description (declared on the scheduler class);
// the technical name and ADRs stay one hover away for whoever needs them.
const JobCell = ({ job }) => {
  const technical = [job.name, job.adrs?.length > 0 ? job.adrs.join(', ') : null].filter(Boolean).join(' · ');
  return (
    <TableCell sx={{ maxWidth: 320 }}>
      <TwoLine>
        <Tooltip arrow title={technical}>
          <TwoLinePrimary>{job.title}</TwoLinePrimary>
        </Tooltip>
        <TwoLineSecondary>{job.description || job.name}</TwoLineSecondary>
      </TwoLine>
    </TableCell>
  );
};

const ScheduleCell = ({ job }) => (
  <TableCell>
    <TwoLine>
      <TwoLinePrimary>{describeSchedule(job.schedule)}</TwoLinePrimary>
      {job.schedule?.type !== 'manual-only' && job.timezone && (
        <TwoLineSecondary>{job.timezone}</TwoLineSecondary>
      )}
    </TwoLine>
  </TableCell>
);

const NextRunCell = ({ job, now }) => {
  if (!job.nextRun) {
    return <TableCell>—</TableCell>;
  }
  const upcoming = (job.upcomingRuns || []).map((d) => formatDateTime(d));
  return (
    <TableCell>
      <Tooltip
        arrow
        title={upcoming.length > 1 ? (
          <span style={{ whiteSpace: 'pre-line' }}>{`Next runs (your local time):\n${upcoming.join('\n')}`}</span>
        ) : ''}
        disableHoverListener={upcoming.length <= 1}
      >
        <TwoLine>
          <TwoLinePrimary>{formatDateTime(job.nextRun)}</TwoLinePrimary>
          <TwoLineSecondary>{formatRelative(job.nextRun, now)}</TwoLineSecondary>
        </TwoLine>
      </Tooltip>
    </TableCell>
  );
};

const LastRunCell = ({ job, now }) => {
  const last = job.lastRun || {};
  if (!last.startedAt) {
    return <TableCell><TwoLineSecondary>Not run since restart</TwoLineSecondary></TableCell>;
  }
  const trigger = last.trigger || 'SCHEDULER';
  return (
    <TableCell>
      <TwoLine>
        <TwoLinePrimary>{formatDateTime(last.startedAt)}</TwoLinePrimary>
        <TwoLineSecondary>
          {`${formatRelative(last.startedAt, now)} · `}
          <span style={SYNC_TRIGGER_COLORS[trigger] ? { color: SYNC_TRIGGER_COLORS[trigger] } : undefined}>
            {describeTrigger({ trigger })}
          </span>
        </TwoLineSecondary>
      </TwoLine>
    </TableCell>
  );
};

const OutcomeCell = ({ job }) => {
  if (job.isRunning) {
    return <TableCell><Chip size="small" label="In progress" sx={getChipSx('info')} /></TableCell>;
  }
  const outcome = job.lastRun?.outcome;
  if (!outcome) {
    return <TableCell>—</TableCell>;
  }
  const chip = (
    <Chip size="small" label={JOB_OUTCOME_LABELS[outcome] || outcome} sx={getChipSx(JOB_OUTCOME_COLORS[outcome])} />
  );
  return (
    <TableCell>
      {job.lastRun?.error ? <Tooltip arrow title={job.lastRun.error}>{chip}</Tooltip> : chip}
      {job.consecutiveFailures > 1 && (
        <Tooltip arrow title="Consecutive failed runs since the last success or restart">
          <Chip size="small" label={`${job.consecutiveFailures}× in a row`} sx={{ ...getChipSx('error'), ml: 0.5 }} />
        </Tooltip>
      )}
      {job.skippedCount > 0 && (
        <Tooltip
          arrow
          title={`A scheduled fire was dropped because the previous run was still going (last: ${formatDateTime(job.lastSkippedAt)})`}
        >
          <Chip size="small" label={`${job.skippedCount} skipped`} sx={{ ...getChipSx('warning'), ml: 0.5 }} />
        </Tooltip>
      )}
    </TableCell>
  );
};

const HistoryCell = ({ job, ability }) => {
  const navigate = useNavigate();
  const known = job.runHistory && RUN_HISTORY_REPORTS[job.runHistory.report];
  // A report behind an off feature flag is treated as absent.
  const target = known && (!known.featureFlag || FEATURE_FLAGS[known.featureFlag]) ? known : null;
  if (!target) {
    return <TableCell><TwoLineSecondary>Logs only</TwoLineSecondary></TableCell>;
  }
  if (!ability.can(target.action, target.subject)) {
    return <TableCell><TwoLineSecondary>{target.label}</TwoLineSecondary></TableCell>;
  }
  return (
    <TableCell>
      <LinkButton type="button" onClick={() => navigate(target.path)}>{target.label}</LinkButton>
    </TableCell>
  );
};

const JobsTable = ({ schedulers, categories, ability, now }) => (
  <TableWrapper>
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Job</TableCell>
            <TableCell>Schedule</TableCell>
            <TableCell>Next run</TableCell>
            <TableCell>Last run</TableCell>
            <TableCell>Outcome</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell>State</TableCell>
            <TableCell>Run history</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groupByCategory(schedulers, categories).map((group) => (
            <React.Fragment key={group.category}>
              <TableRow>
                <TableCell
                  colSpan={8}
                  sx={{ background: '#f8fafc', fontWeight: 600, borderTop: '2px solid #e2e8f0' }}
                >
                  {`${group.label} (${group.jobs.length})`}
                </TableCell>
              </TableRow>
              {group.jobs.map((job) => (
                <TableRow key={job.name} hover>
                  <JobCell job={job} />
                  <ScheduleCell job={job} />
                  <NextRunCell job={job} now={now} />
                  <LastRunCell job={job} now={now} />
                  <OutcomeCell job={job} />
                  <TableCell>{job.isRunning ? '…' : formatDuration(job.lastRun?.durationMs)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={JOB_STATE_LABELS[job.state] || job.state} sx={getChipSx(JOB_STATE_COLORS[job.state])} />
                  </TableCell>
                  <HistoryCell job={job} ability={ability} />
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </TableWrapper>
);

export default JobsTable;
